import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

// ---------------------------------------------------------

import Content from '@components/content';
import Jumbotron from '@components/jumbotron';
import WhereWeServe from '@components/where-we-serve';
import Layout from '@layout/index';
import {
  getServiceProvider,
  getServiceSchemaConfig,
  SERVICE_AREAS,
  SERVICE_AREA_REGIONS,
  SERVICE_PROVIDER_NAME
} from '@lib/service-schema-config';
import { getSiteURL, validateSchemaJSON } from '@lib/utils';
import { extractFaqSectionItemsFromBody } from '@lib/faq-content';
import { generateFAQSchema, generateServiceSchema } from '@lib/generate-schemas';

// ---------------------------------------------------------

export default function TopicPageTemplate(props) {
  const {
    collection = 'Main',
    pageData,
    preview = false,
    coreService,
    specializedPageSchema
  } = props;

  const router = useRouter();

  const collectionHomePathMap = {
    Mortgage: '/mortgage',
    Relocation: '/relocation',
    HomeServices: '/home-services',
    Main: '/'
  };

  // Validate CMS schema from pageData
  const collectionPath = collectionHomePathMap[collection]?.replace('/', '') || 'main';
  const cmsSchema = pageData?.schema
    ? validateSchemaJSON(pageData.schema, `${collectionPath}/${pageData.slug}`)
    : null;

  // The FAQ block is embedded in the body rich text (rendered inline by Content).
  // Extract its Q&A here only to emit the matching FAQPage JSON-LD at the page level.
  const faqItems = extractFaqSectionItemsFromBody(pageData?.body);
  const faqSchema = generateFAQSchema(faqItems);

  // Service/program pages (mortgage, relocation, home services, /programs/*) emit a
  // schema.org Service describing the service, provider, coverage area, audience, and
  // primary CTA. Non-service topic pages (About, Guidance, ...) resolve to null here
  // and emit nothing.
  const serviceConfig = getServiceSchemaConfig(pageData?.topic, pageData?.slug);

  const toAbsoluteUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return url.startsWith('/') ? `${getSiteURL()}${url}` : null;
  };

  const primaryCta = pageData?.headerCtAsCollection?.items?.find((cta) => toAbsoluteUrl(cta?.url));

  // Match the page's canonical URL (Layout derives canonical the same way) so the
  // schema url is correct for both index and [slug] service pages.
  const pagePath = (router?.asPath || '').split(/[?#]/)[0];

  const serviceSchema = serviceConfig
    ? generateServiceSchema({
        schemaType: serviceConfig.schemaType,
        serviceType: serviceConfig.serviceType,
        audience: serviceConfig.audience,
        // Prefer the visible page title/copy over the meta fields for schema
        // parity: metaTitle carries the "| Sibcy Cline" suffix and
        // metaDescription is by definition not rendered on the page.
        name: pageData?.title || pageData?.metaTitle,
        // Program pages render neither the excerpt nor the header CTAs (see the
        // Jumbotron props below), so claiming either in schema would assert
        // content the page never shows.
        description: coreService ? pageData?.excerpt || pageData?.metaDescription : null,
        url: pagePath ? `${getSiteURL()}${pagePath}` : null,
        provider: getServiceProvider(),
        areaServed: SERVICE_AREAS,
        primaryAction:
          coreService && primaryCta
            ? { name: primaryCta.label, url: toAbsoluteUrl(primaryCta.url) }
            : null
      })
    : null;

  return (
    <Layout
      preview={preview}
      collection={collection}
      meta={{
        description: pageData?.metaDescription || pageData?.excerpt,
        image: pageData?.metaImage || pageData?.featuredImage,
        title: pageData?.metaTitle || pageData?.title
      }}
      homePath={collectionHomePathMap[collection] || '/'}
      specializedPageSchema={specializedPageSchema}
      cmsSchema={cmsSchema}
      faqSchema={faqSchema}
      serviceSchema={serviceSchema}
    >
      <Jumbotron
        theme={coreService ? 'simple' : 'bar'}
        image={{
          alt: pageData?.headerImage?.alt,
          src: pageData?.headerImage?.url,
          width: pageData?.headerImage?.width,
          height: pageData?.headerImage?.height
        }}
        body={coreService ? pageData?.excerpt : pageData.topic}
        title={pageData.title}
        ctas={coreService && pageData.headerCtAsCollection?.items}
      />
      <Content isBlockCollection content={pageData.body} />
      {serviceConfig && (
        <WhereWeServe
          regions={SERVICE_AREA_REGIONS}
          audience={serviceConfig.audience}
          introPhrase={serviceConfig.introPhrase}
          providerName={SERVICE_PROVIDER_NAME}
        />
      )}
    </Layout>
  );
}

TopicPageTemplate.propTypes = {
  collection: PropTypes.oneOf(['Mortgage', 'Relocation', 'HomeServices', 'Main']).isRequired,
  pageData: PropTypes.object.isRequired,
  preview: PropTypes.bool,
  coreService: PropTypes.bool,
  specializedPageSchema: PropTypes.string
};
